import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  DataTableEndlessPaginated,
  EmptyStateProps,
} from '@/components/data-table/data-table-endless-paginated';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';
import { AuthenticatedRoutes } from '@/router/routes';

import { TeamMemberListItem } from '../../types/team-member';

interface TeamMembersTableProps {
  table: Table<TeamMemberListItem>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  isAnyFilterApplied?: boolean;
  totalCount?: number;
  onRowClick: (row: TeamMemberListItem) => void;
  getRowHref?: (row: TeamMemberListItem) => string | undefined;
  stickyHeader?: boolean;
  fixedHeight?: boolean;
  tableId?: string;
  onResizeEnd?: () => void;
}

export const TeamMembersTable: React.FC<TeamMembersTableProps> = ({
  isAnyFilterApplied,
  totalCount,
  onRowClick,
  getRowHref,
  stickyHeader = false,
  fixedHeight = false,
  tableId,
  onResizeEnd,
  ...paginatedTableProps
}) => {
  const { t } = useTranslation(['authenticated.team', 'common']);
  const navigate = useNavigate();

  const renderEmptyState = ({ colSpan }: EmptyStateProps): React.ReactNode => {
    // If there's any filter but no results, show "No results found"
    if (isAnyFilterApplied) {
      return (
        <tr>
          <td colSpan={colSpan} className="h-16 w-full px-0">
            <StickyTableStateContent className="px-12 py-4">
              <div className="text-sm">{t('common:noResultsFound')}</div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    // If there are no team members at all (total count is 0), show empty state with button
    if (totalCount === 0) {
      return (
        <tr>
          <td colSpan={colSpan} className="w-full px-0 py-4">
            <StickyTableStateContent className="items-start gap-3 px-8 text-left">
              <div className="flex flex-col gap-0.5">
                <div className="text-sm font-semibold text-foreground">{t('noMembers')}</div>
                <div className="text-xs text-muted-foreground">{t('addFirstMember')}</div>
              </div>
              <Button
                variant="default"
                size="sm"
                className="h-9 gap-2"
                onClick={() => navigate(`${AuthenticatedRoutes.Team}/new`)}
              >
                <CirclePlus className="h-4 w-4" />
                {t('addMember')}
              </Button>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <DataTableEndlessPaginated
      {...paginatedTableProps}
      renderEmptyState={renderEmptyState}
      onRowClick={onRowClick}
      getRowHref={getRowHref}
      stickyHeader={stickyHeader}
      fixedHeight={fixedHeight}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
    />
  );
};
